
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Pill, 
  Heart, 
  Activity, 
  Thermometer,
  Brain, 
  HeartPulse,
  Baby,
  FileText,
  CalendarCheck
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

export const FeatureCards = () => {
  const features = [
    {
      icon: Pill,
      title: "MÉDICAMENTS",
      description: "Suivi et rappels",
      color: "text-blue-600",
      path: "/medicaments"
    },
    {
      icon: CalendarCheck,
      title: "RENDEZ-VOUS",
      description: "Gestion des consultations",
      color: "text-purple-600",
      path: "/rendez-vous"
    },
    {
      icon: FileText,
      title: "JOURNAL",
      description: "Suivi quotidien",
      color: "text-green-600",
      path: "/journal"
    },
    {
      icon: Heart,
      title: "SUIVI DE CYCLE",
      description: "Cycle menstruel",
      color: "text-pink-600",
      path: "/period-tracking"
    },
    {
      icon: Baby,
      title: "GROSSESSE",
      description: "Suivi de grossesse",
      color: "text-teal-600",
      path: "/gestation-tracking"
    },
    {
      icon: Brain,
      title: "IA MÉDICALE",
      description: "Assistance intelligente",
      color: "text-indigo-600",
      path: "/ai-assistance"
    },
    {
      icon: Activity,
      title: "ANALYSES",
      description: "Suivi de vos données de santé",
      color: "text-orange-600",
      path: "/journal"
    },
    {
      icon: Thermometer,
      title: "SYMPTÔMES",
      description: "Suivi de température",
      color: "text-red-600",
      path: "/journal"
    },
    {
      icon: HeartPulse,
      title: "URGENCES",
      description: "Contacts et procédures",
      color: "text-amber-600",
      path: "/virtual-first-aid"
    },
  ];

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolling, setScrolling] = useState(true);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationFrameId: number;
    let scrollAmount = 0;
    const speed = 0.5; // Pixels per frame
    const gap = 16; // Gap between items

    const scroll = () => {
      if (!scrollContainer || !scrolling) return;
      
      scrollAmount += speed;
      
      // When we've scrolled the width of the first item + gap, reset
      const firstItem = scrollContainer.querySelector('.feature-item');
      if (firstItem && scrollAmount >= firstItem.clientWidth + gap) {
        // Reset scroll and move first item to the end
        scrollAmount = 0;
        scrollContainer.appendChild(scrollContainer.children[0].cloneNode(true));
        scrollContainer.removeChild(scrollContainer.children[0]);
      }
      
      scrollContainer.scrollLeft = scrollAmount;
      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [scrolling]);

  const handleMouseEnter = () => {
    setScrolling(false);
  };

  const handleMouseLeave = () => {
    setScrolling(true);
  };

  return (
    <section id="features" className="pt-0 pb-2 px-4 sm:px-8 max-w-7xl mx-auto">
      <div className="text-center mb-1">
        <h2 className="text-3xl font-bold">Catégories</h2>
        <p className="text-muted-foreground mt-2">
          Explorez nos différentes catégories de services de santé
        </p>
      </div>
      
      <div 
        className="relative overflow-hidden"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div 
          ref={scrollRef}
          className="flex items-center space-x-8 py-4 overflow-hidden"
        >
          {[...features, ...features].map((feature, index) => (
            <Link 
              to={feature.path} 
              key={index} 
              className="feature-item flex flex-col items-center flex-shrink-0 cursor-pointer"
            >
              <div className={cn(
                "w-24 h-24 rounded-full flex items-center justify-center mb-3",
                "border shadow-sm hover:shadow-md transition-all transform hover:scale-105",
                "bg-white"
              )}>
                <feature.icon className={cn("h-10 w-10", feature.color)} />
              </div>
              <span className="font-medium text-sm uppercase tracking-wide text-center">
                {feature.title}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
